import React, { useState, useMemo, useEffect } from 'react';
import { Staff } from '../types';
import { StaffSummary } from '../services/summary.service';
import Card from './ui/Card';
import Button from './ui/Button';
import Input from './ui/Input';
import Modal from './ui/Modal';
import ImagePreview from './ui/ImagePreview';
import { useAuth } from '../contexts/AuthContext';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import {
    Users,
    Search,
    Edit,
    Eye,
    Shield,
    ChevronLeft,
    ChevronRight,
    User,
    Mail,
    Phone,
    Calendar,
    DollarSign,
    Plus,
    ToggleLeft,
    ToggleRight,
    UserCheck,
    UserX,
    RefreshCw,
    Trash2,
    MapPin,
    CreditCard,
    GraduationCap
} from 'lucide-react';

interface StaffViewProps {
    staff: Staff[];
    onEdit: (staff: Staff) => void;
    onToggleStatus: (staffId: string, newStatus: 'active' | 'inactive') => void;
    onDelete?: (staffId: string) => void;
    onAddStaff?: () => void;
    loading?: boolean;
    summary?: StaffSummary;
}

const StaffView: React.FC<StaffViewProps> = ({ staff, onEdit, onToggleStatus, onDelete, onAddStaff, loading = false, summary }) => {
    const { user } = useAuth();
    const isSuperAdmin = user?.role === 'super_admin';
    const isAuthorized = user?.role === 'super_admin' || user?.role === 'admin' || user?.role === 'hr' || user?.role === 'principal';
    const isDev = process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const [searchTerm, setSearchTerm] = useState('');
    const [filterRole, setFilterRole] = useState<string>('all');
    const [filterStatus, setFilterStatus] = useState<string>('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [viewingStaff, setViewingStaff] = useState<Staff | null>(null);
    const [showImageModal, setShowImageModal] = useState(false);
    const [selectedImageUrl, setSelectedImageUrl] = useState('');
    const [selectedImageAlt, setSelectedImageAlt] = useState('');
    const [teacherEmails, setTeacherEmails] = useState<Set<string>>(new Set());
    const [contextMenu, setContextMenu] = useState<{
        x: number;
        y: number;
        member: Staff;
    } | null>(null);

    useEffect(() => {
        const handleClose = () => setContextMenu(null);
        window.addEventListener('click', handleClose);
        return () => window.removeEventListener('click', handleClose);
    }, []);

    useEffect(() => {
        const fetchTeacherEmails = async () => {
            try {
                const teachersRef = collection(db, 'teachers');
                const querySnapshot = await getDocs(teachersRef);
                const emails = new Set<string>();
                querySnapshot.docs.forEach(doc => {
                    const data = doc.data();
                    if (data.email) {
                        emails.add(data.email.toLowerCase().trim());
                    }
                });
                setTeacherEmails(emails);
            } catch (error) {
                console.error('Error fetching teacher emails:', error);
            }
        };
        fetchTeacherEmails();
    }, []);

    const handleImageClick = (imageUrl: string, staffName: string) => {
        setSelectedImageUrl(imageUrl);
        setSelectedImageAlt(`${staffName} - Profile Image`);
        setShowImageModal(true);
    };

    // Filter and search staff
    const filteredStaff = useMemo(() => {
        return staff.filter(member => {
            const matchesSearch =
                member.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                member.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                member.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                member.staffId.toLowerCase().includes(searchTerm.toLowerCase());

            const matchesRole = filterRole === 'all' || member.role === filterRole;
            const matchesStatus = filterStatus === 'all' || member.currentPosition.status === filterStatus;

            return matchesSearch && matchesRole && matchesStatus;
        });
    }, [staff, searchTerm, filterRole, filterStatus]);

    // Pagination
    const totalPages = Math.ceil(filteredStaff.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedStaff = filteredStaff.slice(startIndex, startIndex + itemsPerPage);

    const getPaginationRange = () => {
        const delta = 1;
        const range: (number | string)[] = [];
        const rangeWithDots: (number | string)[] = [];
        let l: number | undefined;

        for (let i = 1; i <= totalPages; i++) {
            if (i === 1 || i === totalPages || (i >= currentPage - delta && i <= currentPage + delta)) {
                range.push(i);
            }
        }

        for (const i of range) {
            if (l !== undefined) {
                if (i as number - l === 2) {
                    rangeWithDots.push(l + 1);
                } else if (i as number - l > 2) {
                    rangeWithDots.push('...');
                }
            }
            rangeWithDots.push(i);
            l = i as number;
        }

        return rangeWithDots;
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const handleItemsPerPageChange = (value: string) => {
        setItemsPerPage(parseInt(value));
        setCurrentPage(1);
    };

    const getRoleBadgeColor = (role: string) => {
        switch (role) {
            case 'admin': return 'bg-red-100 text-red-800';
            case 'hr': return 'bg-blue-100 text-blue-800';
            case 'teacher': return 'bg-green-100 text-green-800';
            case 'accountant': return 'bg-purple-100 text-purple-800';
            case 'principal': return 'bg-yellow-100 text-yellow-800';
            case 'clerk': return 'bg-gray-100 text-gray-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const getStatusBadgeColor = (status: string) => {
        switch (status) {
            case 'active': return 'bg-green-100 text-green-800';
            case 'inactive': return 'bg-yellow-100 text-yellow-800';
            case 'resigned': return 'bg-red-100 text-red-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const getAuthorityCount = (authorities: { [key: string]: boolean }) => {
        return Object.values(authorities).filter(Boolean).length;
    };

    const handleStatusToggle = (staffId: string, currentStatus: string) => {
        const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
        onToggleStatus(staffId, newStatus as 'active' | 'inactive');
    };

    const StaffCard: React.FC<{ member: Staff }> = ({ member }) => (
        <Card glassy={true} className="islamic-pattern group hover:scale-105 transition-transform duration-300">
            <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 rounded-full overflow-hidden shrink-0 cursor-pointer"
                             onClick={() => member.profileImage && handleImageClick(member.profileImage, `${member.firstName} ${member.lastName}`)}>
                            {member.profileImage ? (
                                <img src={member.profileImage} alt="" className="w-full h-full object-cover" />
                            ) : (
                                <div className="w-full h-full bg-gradient-to-r from-emerald-500 to-teal-600 flex items-center justify-center text-white font-bold text-sm">
                                    {`${member.firstName[0] ?? ''}${member.lastName?.[0] ?? ''}`.toUpperCase()}
                                </div>
                            )}
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900">
                                {member.firstName} {member.lastName}
                            </h3>
                            <p className="text-sm text-gray-600">{member.staffId}</p>
                        </div>
                    </div>
                    <div className="flex space-x-1">
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => setViewingStaff(member)}
                        >
                            <Eye className="h-4 w-4" />
                        </Button>
                        {isAuthorized ? (
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => onEdit(member)}
                            >
                                <Edit className="h-4 w-4" />
                            </Button>
                        ) : (
                            <span title="Contact Dynamix Zone for access this" className="inline-block">
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    className="opacity-40 cursor-not-allowed"
                                    disabled
                                >
                                    <Edit className="h-4 w-4" />
                                </Button>
                            </span>
                        )}
                        {isAuthorized ? (
                            <button
                                onClick={() => handleStatusToggle(member.id, member.currentPosition.status)}
                                className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                                title={`Switch to ${member.currentPosition.status === 'active' ? 'inactive' : 'active'}`}
                            >
                                {member.currentPosition.status === 'active' ?
                                    <ToggleRight className="h-4 w-4 text-green-600" /> :
                                    <ToggleLeft className="h-4 w-4 text-gray-400" />
                                }
                            </button>
                        ) : (
                            <span title="Contact Dynamix Zone for access this" className="inline-block">
                                <button
                                    className="p-1.5 rounded-lg opacity-40 cursor-not-allowed"
                                    disabled
                                >
                                    {member.currentPosition.status === 'active' ?
                                        <ToggleRight className="h-4 w-4 text-green-600" /> :
                                        <ToggleLeft className="h-4 w-4 text-gray-400" />
                                    }
                                </button>
                            </span>
                        )}
                        {isSuperAdmin && onDelete && (
                            <button
                                onClick={() => onDelete(member.id)}
                                className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 transition-colors"
                                title="Delete Staff Member"
                            >
                                <Trash2 className="h-4 w-4" />
                            </button>
                        )}
                    </div>
                </div>

                <div className="space-y-3 mb-4">
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <Mail className="h-4 w-4" />
                        <span>{member.email}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <Phone className="h-4 w-4" />
                        <span>{member.phone}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <Calendar className="h-4 w-4" />
                        <span>{member.currentPosition.department} - {member.currentPosition.designation}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <DollarSign className="h-4 w-4" />
                        <span>${member.currentPosition.salary.toLocaleString()}</span>
                    </div>
                </div>

                <div className="flex items-center justify-between">
                    <div className="flex space-x-2 flex-wrap gap-1">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleBadgeColor(member.role)}`}>
                            {member.role.toUpperCase()}
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeColor(member.currentPosition.status)}`}>
                            {member.currentPosition.status}
                        </span>
                        {teacherEmails.has(member.email.toLowerCase().trim()) && (
                            <span className="px-2 py-1 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-800">
                                Dual Role: Staff & Teacher
                            </span>
                        )}
                    </div>
                    <div className="flex items-center space-x-1 text-xs text-gray-500">
                        <Shield className="h-3 w-3" />
                        <span>{getAuthorityCount(member.authorities)} permissions</span>
                    </div>
                </div>
            </div>
        </Card>
    );

    const StaffTableRow: React.FC<{ member: Staff }> = ({ member }) => (
        <tr
            className="hover:bg-gray-50/70 transition-colors duration-200 cursor-pointer select-none"
            onClick={() => setViewingStaff(member)}
            onContextMenu={(e) => {
                e.preventDefault();
                setContextMenu({
                    x: e.clientX,
                    y: e.clientY,
                    member: member
                });
            }}
        >
            <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 rounded-full overflow-hidden shrink-0 cursor-pointer"
                         onClick={(e) => {
                             e.stopPropagation();
                             if (member.profileImage) {
                                 handleImageClick(member.profileImage, `${member.firstName} ${member.lastName}`);
                             }
                         }}>
                        {member.profileImage ? (
                            <img src={member.profileImage} alt="" className="w-full h-full object-cover" />
                        ) : (
                            <div className="w-full h-full bg-gradient-to-r from-emerald-500 to-teal-600 flex items-center justify-center text-white font-bold text-xs">
                                {`${member.firstName[0] ?? ''}${member.lastName?.[0] ?? ''}`.toUpperCase()}
                            </div>
                        )}
                    </div>
                    <div>
                        <div className="text-sm font-medium text-gray-900">
                            {member.firstName} {member.lastName}
                        </div>
                        <div className="text-sm text-gray-500">{member.staffId}</div>
                    </div>
                </div>
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900">{member.email}</div>
                <div className="text-sm text-gray-500">{member.phone}</div>
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900">{member.currentPosition.department}</div>
                <div className="text-sm text-gray-500">{member.currentPosition.designation}</div>
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleBadgeColor(member.role)}`}>
                    {member.role.toUpperCase()}
                </span>
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex flex-col gap-1 items-start">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeColor(member.currentPosition.status)}`}>
                        {member.currentPosition.status}
                    </span>
                    {teacherEmails.has(member.email.toLowerCase().trim()) && (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-indigo-100 text-indigo-800">
                            Dual Role
                        </span>
                    )}
                </div>
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                ${member.currentPosition.salary.toLocaleString()}
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center space-x-1 text-xs text-gray-500">
                    <Shield className="h-3 w-3" />
                    <span>{getAuthorityCount(member.authorities)}</span>
                </div>
            </td>
        </tr>
    );

    return (
        <div className="space-y-5">
            {/* Page header */}
            <div className="flex items-start justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Staff</h1>
                    <p className="text-sm text-gray-500 mt-0.5">Manage your school staff and their permissions.</p>
                </div>
                {onAddStaff && (
                    <button onClick={onAddStaff} className="flex items-center gap-1.5 bg-red-500 hover:bg-red-600 text-white text-sm font-semibold px-4 py-2.5 rounded-xl shadow-sm transition-colors">
                        <Plus className="h-4 w-4" />Create Staff
                    </button>
                )}
            </div>

            {/* Stat cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 flex items-center gap-4">
                    <div className="w-11 h-11 rounded-lg flex items-center justify-center bg-blue-50 text-blue-500 shrink-0"><Users className="h-5 w-5" /></div>
                    <div><p className="text-xs text-gray-500 font-medium">Total Staff</p><p className="text-2xl font-bold text-gray-900 mt-0.5">{summary?.total ?? staff.length}</p></div>
                </div>
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 flex items-center gap-4">
                    <div className="w-11 h-11 rounded-lg flex items-center justify-center bg-emerald-50 text-emerald-600 shrink-0"><UserCheck className="h-5 w-5" /></div>
                    <div><p className="text-xs text-gray-500 font-medium">Active Staff</p><p className="text-2xl font-bold text-gray-900 mt-0.5">{summary?.active ?? staff.filter(s => s.currentPosition.status === 'active').length}</p></div>
                </div>
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 flex items-center gap-4">
                    <div className="w-11 h-11 rounded-lg flex items-center justify-center bg-red-50 text-red-500 shrink-0"><UserX className="h-5 w-5" /></div>
                    <div><p className="text-xs text-gray-500 font-medium">Inactive Staff</p><p className="text-2xl font-bold text-gray-900 mt-0.5">{summary?.inactive ?? staff.filter(s => s.currentPosition.status === 'inactive').length}</p></div>
                </div>
            </div>

            {/* Table card */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                {/* Toolbar */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                    <h2 className="text-base font-bold text-gray-900">Staff Information</h2>
                    <div className="flex items-center gap-3">
                        <div className="relative">
                            <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                            <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search" className="pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 w-48" />
                        </div>
                        <select value={filterRole} onChange={e => setFilterRole(e.target.value)} className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white">
                            <option value="all">All Roles</option>
                            <option value="admin">Admin</option>
                            <option value="hr">HR</option>
                            <option value="accountant">Accountant</option>
                            <option value="principal">Principal</option>
                            <option value="clerk">Clerk</option>
                        </select>
                        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white">
                            <option value="all">All Status</option>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                            <option value="resigned">Resigned</option>
                        </select>
                        <button className="p-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 transition-colors"><RefreshCw className="h-4 w-4" /></button>
                    </div>
                </div>

                {loading ? (
                    <div className="flex justify-center items-center py-16">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-gray-100 bg-gray-50/60">
                                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Staff Member</th>
                                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Contact</th>
                                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Position</th>
                                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Role</th>
                                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Salary</th>
                                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Permissions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {paginatedStaff.length === 0 ? (
                                    <tr><td colSpan={7} className="px-5 py-12 text-center text-gray-400 text-sm"><Users className="h-8 w-8 mx-auto mb-2 opacity-30" />No staff found</td></tr>
                                ) : paginatedStaff.map((member) => (
                                    <StaffTableRow key={member.id} member={member} />
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Pagination footer */}
                <div className="flex items-center justify-between px-5 py-3.5 border-t border-gray-100 bg-gray-50/40">
                    <span className="text-xs text-gray-500">
                        {filteredStaff.length === 0 ? '0' : `${startIndex + 1}-${Math.min(startIndex + itemsPerPage, filteredStaff.length)}`} of {filteredStaff.length} items
                    </span>
                    <div className="flex items-center gap-2">
                        <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}
                            className="p-1.5 rounded-lg border border-gray-200 text-gray-500 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                            <ChevronLeft className="h-4 w-4" />
                        </button>
                        
                        {getPaginationRange().map((page, idx) => {
                            if (page === '...') {
                                return (
                                    <span key={idx} className="px-1 text-gray-400 text-sm font-semibold select-none">
                                        ...
                                    </span>
                                );
                            }
                            return (
                                <button
                                    key={idx}
                                    onClick={() => handlePageChange(page as number)}
                                    className={`w-7 h-7 rounded-lg text-xs font-bold flex items-center justify-center transition-all ${
                                        currentPage === page
                                            ? 'bg-red-500 text-white shadow-sm'
                                            : 'border border-gray-200 text-gray-600 hover:bg-gray-50'
                                    }`}
                                >
                                    {page}
                                </button>
                            );
                        })}

                        <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages || totalPages === 0}
                            className="p-1.5 rounded-lg border border-gray-200 text-gray-500 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                            <ChevronRight className="h-4 w-4" />
                        </button>
                        <select value={itemsPerPage} onChange={e => handleItemsPerPageChange(e.target.value)}
                            className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-emerald-500 bg-white">
                            <option value={10}>10 / page</option>
                            <option value={25}>25 / page</option>
                            <option value={50}>50 / page</option>
                        </select>
                    </div>
                </div>
            </div>



            {/* Read-only View Modal */}
            {viewingStaff && (
                <Modal
                    isOpen={true}
                    onClose={() => setViewingStaff(null)}
                    title="Staff Information"
                    size="lg"
                    footer={
                        <>
                            <Button type="button" variant="secondary" size="sm" onClick={() => setViewingStaff(null)}>Close</Button>
                            {isAuthorized && (
                                <Button type="button" size="sm" onClick={() => { setViewingStaff(null); onEdit(viewingStaff); }} className="flex items-center space-x-2">
                                    <Edit className="h-4 w-4" /><span>Edit Staff</span>
                                </Button>
                            )}
                        </>
                    }
                >
                    <div className="space-y-6">
                        {/* Basic Information */}
                        <div>
                            <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3 flex items-center border-b pb-1.5">
                                <User className="h-4 w-4 mr-2 text-emerald-600" />Basic Information
                            </h4>
                            <div className="flex justify-center mb-5">
                                <div className="w-20 h-20 rounded-full overflow-hidden cursor-pointer ring-4 ring-emerald-50 shadow-md"
                                    onClick={() => viewingStaff.profileImage && handleImageClick(viewingStaff.profileImage, `${viewingStaff.firstName} ${viewingStaff.lastName}`)}>
                                    {viewingStaff.profileImage ? (
                                        <img src={viewingStaff.profileImage} alt="" className="w-full h-full object-cover" />
                                    ) : (
                                        <div className="w-full h-full bg-emerald-500 flex items-center justify-center text-white text-2xl font-bold">
                                            {`${viewingStaff.firstName[0]}${viewingStaff.lastName?.[0] ?? ''}`.toUpperCase()}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
                                {[
                                    ['Staff ID', viewingStaff.staffId],
                                    ['First Name', viewingStaff.firstName],
                                    ['Last Name', viewingStaff.lastName || 'N/A'],
                                    ['Email', viewingStaff.email],
                                    ['Phone', viewingStaff.phone || 'N/A'],
                                    ['Date of Birth', viewingStaff.dateOfBirth ? new Date(viewingStaff.dateOfBirth).toLocaleDateString() : 'N/A'],
                                    ['Gender', viewingStaff.gender],
                                    ['Role', viewingStaff.role],
                                    ['RFID Card Number', viewingStaff.cardNumber || 'N/A']
                                ].map(([label, val]) => (
                                    <div key={label}>
                                        <span className="text-gray-500 block mb-1">{label}</span>
                                        <div className="px-3 py-2 border border-gray-200 rounded-xl bg-gray-50 text-gray-800 font-medium capitalize truncate">{val}</div>
                                    </div>
                                ))}
                                {teacherEmails.has(viewingStaff.email.toLowerCase().trim()) && (
                                    <div>
                                        <span className="text-indigo-600 font-bold block mb-1">Dual Role Status</span>
                                        <div className="px-3 py-2 border border-indigo-200 rounded-xl bg-indigo-50 text-indigo-800 font-semibold truncate">
                                            Also Registered as Teacher
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Professional Information */}
                        <div>
                            <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3 flex items-center border-b pb-1.5">
                                <GraduationCap className="h-4 w-4 mr-2 text-emerald-600" />Professional Information
                            </h4>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
                                {[
                                    ['Education', viewingStaff.qualification?.education || 'N/A'],
                                    ['Experience (Years)', viewingStaff.experience?.totalYears || '0'],
                                    ['Department', viewingStaff.currentPosition?.department || 'N/A'],
                                    ['Designation', viewingStaff.currentPosition?.designation || 'N/A'],
                                    ['Joining Date', viewingStaff.currentPosition?.joiningDate ? new Date(viewingStaff.currentPosition.joiningDate).toLocaleDateString() : 'N/A'],
                                    ['Salary', viewingStaff.currentPosition?.salary ? `$${viewingStaff.currentPosition.salary.toLocaleString()}` : 'N/A'],
                                    ['Status', viewingStaff.currentPosition?.status || 'N/A'],
                                ].map(([label, val]) => (
                                    <div key={label}>
                                        <span className="text-gray-500 block mb-1">{label}</span>
                                        <div className="px-3 py-2 border border-gray-200 rounded-xl bg-gray-50 text-gray-800 font-medium capitalize">{val}</div>
                                    </div>
                                ))}
                            </div>
                            
                            {/* Certifications */}
                            {viewingStaff.qualification?.certifications && viewingStaff.qualification.certifications.length > 0 && (
                                <div className="mt-3 text-xs">
                                    <span className="text-gray-500 block mb-1 font-medium">Certifications</span>
                                    <div className="flex flex-wrap gap-2">
                                        {viewingStaff.qualification.certifications.map((cert, index) => (
                                            <span
                                                key={index}
                                                className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800"
                                            >
                                                {cert}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Address Information */}
                        <div>
                            <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3 flex items-center border-b pb-1.5">
                                <MapPin className="h-4 w-4 mr-2 text-emerald-600" />Address
                            </h4>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
                                <div className="md:col-span-3">
                                    <span className="text-gray-500 block mb-1">Street</span>
                                    <div className="px-3 py-2 border border-gray-200 rounded-xl bg-gray-50 text-gray-800 font-medium">{viewingStaff.address?.street || 'N/A'}</div>
                                </div>
                                {[
                                    ['City', viewingStaff.address?.city || 'N/A'],
                                    ['State', viewingStaff.address?.state || 'N/A'],
                                    ['ZIP Code', viewingStaff.address?.zipCode || 'N/A'],
                                    ['Country', viewingStaff.address?.country || 'N/A'],
                                ].map(([label, val]) => (
                                    <div key={label}>
                                        <span className="text-gray-500 block mb-1">{label}</span>
                                        <div className="px-3 py-2 border border-gray-200 rounded-xl bg-gray-50 text-gray-800 font-medium">{val}</div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Emergency Contact */}
                        <div>
                            <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3 flex items-center border-b pb-1.5">
                                <Users className="h-4 w-4 mr-2 text-emerald-600" />Emergency Contact
                            </h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                                {[
                                    ['Contact Name', viewingStaff.emergencyContact?.name || 'N/A'],
                                    ['Relationship', viewingStaff.emergencyContact?.relationship || 'N/A'],
                                    ['Phone', viewingStaff.emergencyContact?.phone || 'N/A'],
                                    ['Email', viewingStaff.emergencyContact?.email || 'N/A'],
                                ].map(([label, val]) => (
                                    <div key={label}>
                                        <span className="text-gray-500 block mb-1">{label}</span>
                                        <div className="px-3 py-2 border border-gray-200 rounded-xl bg-gray-50 text-gray-800 font-medium">{val}</div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Bank Details */}
                        <div>
                            <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3 flex items-center border-b pb-1.5">
                                <CreditCard className="h-4 w-4 mr-2 text-emerald-600" />Bank Details
                            </h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                                {[
                                    ['Account Number', viewingStaff.bankDetails?.accountNumber || 'N/A'],
                                    ['Bank Name', viewingStaff.bankDetails?.bankName || 'N/A'],
                                    ['IFSC Code', viewingStaff.bankDetails?.ifscCode || 'N/A'],
                                    ['Account Holder Name', viewingStaff.bankDetails?.accountHolderName || 'N/A'],
                                ].map(([label, val]) => (
                                    <div key={label}>
                                        <span className="text-gray-500 block mb-1">{label}</span>
                                        <div className="px-3 py-2 border border-gray-200 rounded-xl bg-gray-50 text-gray-800 font-medium">{val}</div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Authorities */}
                        <div>
                            <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3 flex items-center border-b pb-1.5">
                                <Shield className="h-4 w-4 mr-2 text-emerald-600" />
                                Permissions ({getAuthorityCount(viewingStaff.authorities)})
                            </h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                                {Object.entries(viewingStaff.authorities).map(([authority, hasPermission]) => (
                                    <div key={authority} className="flex items-center space-x-3 p-3 rounded-xl border border-gray-200">
                                        <div className={`w-3 h-3 rounded-full ${hasPermission ? 'bg-emerald-500' : 'bg-gray-300'}`}></div>
                                        <div className="flex-1">
                                            <div className="text-xs font-semibold text-gray-900 capitalize">
                                                {authority.replace(/([A-Z])/g, ' $1').trim()}
                                            </div>
                                            <div className="text-[10px] text-gray-500">
                                                {hasPermission ? 'Granted' : 'Not granted'}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Image preview */}
            <ImagePreview src={selectedImageUrl} alt={selectedImageAlt} visible={showImageModal} onVisibleChange={setShowImageModal} />

            {/* Context menu for row right click */}
            {contextMenu && (
                <div
                    className="fixed bg-white border border-gray-200 rounded-xl shadow-xl py-1.5 z-50 text-xs font-semibold text-gray-700 min-w-[140px] divide-y divide-gray-50 overflow-hidden animate-in fade-in zoom-in-95 duration-100"
                    style={{ top: contextMenu.y, left: contextMenu.x }}
                >
                    <button
                        onClick={() => {
                            setViewingStaff(contextMenu.member);
                            setContextMenu(null);
                        }}
                        className="w-full text-left px-4 py-2 hover:bg-slate-50 flex items-center gap-2 transition-colors text-gray-700"
                    >
                        <Eye className="w-3.5 h-3.5 text-gray-400" />
                        <span>View Details</span>
                    </button>
                    {isAuthorized && (
                        <button
                            onClick={() => {
                                onEdit(contextMenu.member);
                                setContextMenu(null);
                            }}
                            className="w-full text-left px-4 py-2 hover:bg-slate-50 flex items-center gap-2 transition-colors text-emerald-600 hover:text-emerald-700"
                        >
                            <Edit className="w-3.5 h-3.5 text-emerald-500" />
                            <span>Edit Staff</span>
                        </button>
                    )}
                    {isAuthorized && (
                        <button
                            onClick={() => {
                                handleStatusToggle(contextMenu.member.id, contextMenu.member.currentPosition.status);
                                setContextMenu(null);
                            }}
                            className="w-full text-left px-4 py-2 hover:bg-slate-50 flex items-center gap-2 transition-colors text-gray-700"
                        >
                            {contextMenu.member.currentPosition.status === 'active' ? (
                                <>
                                    <ToggleLeft className="w-3.5 h-3.5 text-gray-400" />
                                    <span>Deactivate</span>
                                </>
                            ) : (
                                <>
                                    <ToggleRight className="w-3.5 h-3.5 text-green-500" />
                                    <span>Activate</span>
                                </>
                            )}
                        </button>
                    )}
                    {isSuperAdmin && onDelete && (
                        <button
                            onClick={() => {
                                onDelete(contextMenu.member.id);
                                setContextMenu(null);
                            }}
                            className="w-full text-left px-4 py-2 hover:bg-red-50 flex items-center gap-2 transition-colors text-red-600 hover:text-red-700"
                        >
                            <Trash2 className="w-3.5 h-3.5 text-red-500" />
                            <span>Delete Staff</span>
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default StaffView;
